import { useState, useEffect } from 'react';
import { useI18n, useNamespace } from '../context/I18nContext';
import { useSearchParams, navigate } from '../shared/lib/router';
import { search } from '../shared/api/searchApi';
import { ENTITY_LINK_PATHS } from '../shared/lib/entityLinks';
import { formatSearchResultLabel } from '../shared/lib/searchResultLabel';

const FULL_PAGE_LIMIT = 25;

/**
 * Account-level page (like SecuritySettingsPage/NotificationsPage) -- not
 * gated by any module/permission. Content is already permission-filtered
 * per section by the backend (an inaccessible type never appears as a key
 * in the response at all), so this page just renders whatever it gets.
 */
export default function SearchResultsPage() {
  useNamespace('search');
  const { t } = useI18n();
  const params = useSearchParams();
  const term = params.get('q') || '';

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (term.length < 2) {
      setResults({});
      setLoading(false);
      return;
    }
    setLoading(true);
    search(term, FULL_PAGE_LIMIT)
      .then((res) => setResults(res.data))
      .finally(() => setLoading(false));
  }, [term]);

  const handleResultClick = (type, item) => {
    const resolvedType = type === 'document' ? item.entityType : type;
    const resolvedId = type === 'document' ? item.entityId : item.id;
    const toPath = ENTITY_LINK_PATHS[resolvedType];
    if (toPath) navigate(toPath(resolvedId));
  };

  const resultEntries = results ? Object.entries(results).filter(([, items]) => items.length > 0) : [];

  return (
    <div className="page">
      <div className="page__header">
        <h1 className="page__title">{t('search', 'resultsTitle')}</h1>
        <p className="form-hint">{t('search', 'resultsFor', { term })}</p>
      </div>

      {loading ? (
        <p className="empty-state">{t('common', 'actions.loading')}</p>
      ) : resultEntries.length === 0 ? (
        <p className="empty-state">{t('search', 'emptyState')}</p>
      ) : (
        resultEntries.map(([type, items]) => (
          <div key={type} className="tab-panel-content">
            <h3 className="section-title">
              {t('search', `types.${type}`)} ({items.length})
            </h3>
            <ul className="record-list">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="record-list__item record-list__item--clickable"
                  onClick={() => handleResultClick(type, item)}
                >
                  <div className="record-list__main">
                    <span className="record-list__title">{formatSearchResultLabel(type, item)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}
